import Phaser from 'phaser';
import { DAY_DURATION } from '../data/world-config';

export class DayNightSystem {
  private scene: Phaser.Scene;
  private overlay: Phaser.GameObjects.Rectangle;
  private timeOfDay: number = 0; // 0 = sunrise (6AM), 0.5 = sunset (6PM), 1.0 = next sunrise
  private dayCount: number = 1;
  private elapsed: number = 0;
  // Summer setting: long days, short nights.
  // Day portion (0.0–0.6) = 60% of cycle, Night portion (0.6–1.0) = 40%.
  // With DAY_DURATION = 300s, full cycle = DAY_DURATION * 1.6 = 480s (8 minutes).
  // Night (isNight 0.55–0.85) is ~30% of 480s ≈ 2.4 minutes of real time.
  private fullCycleDuration: number = DAY_DURATION * 1.6;

  // Colors for different times of day
  private dayColor = 0x000000;   // No tint during day
  private duskColor = 0x331a00;  // Orange-ish dusk
  private nightColor = 0x000033; // Deep blue night
  private dawnColor = 0x1a0033;  // Purple dawn

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Create an oversized overlay that covers any viewport (fixed large size)
    const overlaySize = 8000;
    this.overlay = scene.add.rectangle(
      -overlaySize / 2, -overlaySize / 2,
      overlaySize, overlaySize,
      this.nightColor
    );
    this.overlay.setScrollFactor(0);
    this.overlay.setBlendMode(Phaser.BlendModes.MULTIPLY);
    this.overlay.setAlpha(0);
    this.overlay.setDepth(900);
    this.overlay.setOrigin(0, 0);
  }

  update(_time: number, delta: number): void {
    this.elapsed += delta;

    // Calculate time of day as 0-1 value
    this.timeOfDay = (this.elapsed % this.fullCycleDuration) / this.fullCycleDuration;

    // Check for new day
    const newDayCount = Math.floor(this.elapsed / this.fullCycleDuration) + 1;
    if (newDayCount !== this.dayCount) {
      this.dayCount = newDayCount;
      this.scene.events.emit('new-day', this.dayCount);
    }

    // Calculate overlay alpha and tint based on time of day
    this.updateOverlay();

    // Emit time update for UI
    this.scene.events.emit('time-update', {
      timeOfDay: this.timeOfDay,
      dayCount: this.dayCount,
      isNight: this.isNight(),
    });
  }

  private updateOverlay(): void {
    const t = this.timeOfDay;
    let alpha: number;
    let tintColor: number;

    // Summer schedule: long bright day, short night
    // 0.0–0.45: Full daylight (sunrise through afternoon)
    // 0.45–0.55: Dusk transition
    // 0.55–0.85: Night (short summer night)
    // 0.85–0.95: Dawn transition
    // 0.95–1.0: Early morning → next day
    if (t < 0.45) {
      // Daylight (6AM – 7PM): long summer day
      alpha = 0;
      tintColor = this.dayColor;
    } else if (t < 0.50) {
      // Late evening dusk (7PM – 8PM): gradual orange
      const duskProgress = (t - 0.45) / 0.05;
      alpha = duskProgress * 0.2;
      tintColor = this.lerpColor(this.dayColor, this.duskColor, duskProgress);
    } else if (t < 0.55) {
      // Dusk to night (8PM – 9PM): orange to blue
      const nightProgress = (t - 0.50) / 0.05;
      alpha = 0.2 + nightProgress * 0.5;
      tintColor = this.lerpColor(this.duskColor, this.nightColor, nightProgress);
    } else if (t < 0.85) {
      // Night (9PM – 4AM): short summer night
      alpha = 0.7;
      tintColor = this.nightColor;
    } else if (t < 0.92) {
      // Dawn (4AM – 5AM): blue to purple
      const dawnProgress = (t - 0.85) / 0.07;
      alpha = 0.7 - dawnProgress * 0.3;
      tintColor = this.lerpColor(this.nightColor, this.dawnColor, dawnProgress);
    } else {
      // Early morning (5AM – 6AM): purple to clear
      const morningProgress = (t - 0.92) / 0.08;
      alpha = 0.4 - morningProgress * 0.4;
      tintColor = this.lerpColor(this.dawnColor, this.dayColor, morningProgress);
    }

    this.overlay.setAlpha(alpha);
    this.overlay.setFillStyle(tintColor);
  }

  private lerpColor(color1: number, color2: number, t: number): number {
    const r1 = (color1 >> 16) & 0xff;
    const g1 = (color1 >> 8) & 0xff;
    const b1 = color1 & 0xff;
    const r2 = (color2 >> 16) & 0xff;
    const g2 = (color2 >> 8) & 0xff;
    const b2 = color2 & 0xff;

    const r = Math.floor(r1 + (r2 - r1) * t);
    const g = Math.floor(g1 + (g2 - g1) * t);
    const b = Math.floor(b1 + (b2 - b1) * t);

    return (r << 16) | (g << 8) | b;
  }

  isNight(): boolean {
    return this.timeOfDay >= 0.55 && this.timeOfDay < 0.85;
  }

  getTimeOfDay(): number {
    return this.timeOfDay;
  }

  getDayCount(): number {
    return this.dayCount;
  }
}
